import { useState } from 'react';
import PropTypes from 'prop-types';
import './EditReaderForm.css';

const EditReaderForm = ({ reader, onSave, onCancel, isPrimary }) => {
  const [formData, setFormData] = useState({
    name: reader.name || '',
    age: reader.age?.toString() || '',
    gender: reader.gender || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Reader name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const submitData = {
        name: formData.name.trim(),
      };

      // Only include age if provided and valid
      if (formData.age && parseInt(formData.age) > 0) {
        submitData.age = parseInt(formData.age);
      } else if (formData.age === '') {
        submitData.age = null;
      }

      // Only include gender if selected
      if (formData.gender) {
        submitData.gender = formData.gender;
      } else if (formData.gender === '') {
        submitData.gender = null;
      }
      
      await onSave(submitData);
    } catch (err) {
      console.error('Error updating reader:', err);
      setError(err.response?.data?.error || 'Failed to update reader');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-reader-form">
      <div className="form-header">
        <h3>Edit reader</h3>
        <p>Update information for {reader.name}</p>
        {isPrimary && (
          <span className="primary-notice">
            This is your primary reader account.
          </span>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter reader's name"
            maxLength={100}
            required
            disabled={loading}
          />
          <small className="form-text">
            The name of the reader.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="age">
            Age (optional)
          </label>
          <input
            type="number"
            id="age"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            placeholder="Enter age"
            min="1"
            max="150"
            disabled={loading}
          />
          <small className="form-text">
            Age in years (leave empty to remove).
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="gender">
            Gender (optional)
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
          <small className="form-text">
            Gender identification (select empty to remove).
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

EditReaderForm.propTypes = {
  reader: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    age: PropTypes.number,
    gender: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isPrimary: PropTypes.bool.isRequired,
};

export default EditReaderForm;
